
import React, { useEffect, useState } from 'react';
import { NotificationPreferences as NotificationPrefsType } from '@/contexts/auth/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Bell } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { loadProfessions } from '@/services/professionService';
import { Profession } from '@/lib/professionTypes';
import FrequencySection from './FrequencySection';
import NotificationMethodSection from './NotificationMethodSection';
import ProfessionPreferencesSection from './ProfessionPreferencesSection';
import { areBrowserNotificationsSupported, hasNotificationPermission } from '@/services/browserNotificationService';

interface NotificationPreferencesProps {
  preferences: NotificationPrefsType;
  onChange: (preferences: NotificationPrefsType) => void;
  className?: string;
}

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({
  preferences,
  onChange,
  className
}) => {
  const [browserNotificationsSupported, setBrowserNotificationsSupported] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(false);
  const [professions, setProfessions] = useState<Profession[]>([]);

  useEffect(() => {
    setBrowserNotificationsSupported(areBrowserNotificationsSupported());
    setNotificationPermission(hasNotificationPermission());
  }, []);

  useEffect(() => {
    const fetchProfessions = async () => {
      const data = await loadProfessions();
      setProfessions(data);
    };
    fetchProfessions();
  }, []);

  const handleEnableChange = (enabled: boolean) => {
    onChange({ ...preferences, enabled });
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row md:items-center md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Értesítési beállítások
          </CardTitle>
          <CardDescription>
            Állítsd be, hogyan szeretnél értesülni az új eseményekről
          </CardDescription>
        </div>
        <div className="flex items-center justify-start gap-4">
          <Label htmlFor="notifications-enabled" className="">
            Értesítések engedélyezése
          </Label>
          <Switch
            id="notifications-enabled"
            checked={preferences.enabled}
            onCheckedChange={handleEnableChange}
          />
        </div>

      </CardHeader>
      <CardContent className="py-8">

        {preferences.enabled && (
          <div className='grid grid-cols-4 gap-4'>
            <div className="col-span-4 md:col-span-3">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                <FrequencySection
                  preferences={preferences}
                  onChange={onChange}
                />


                <NotificationMethodSection
                  preferences={preferences}
                  onChange={onChange}
                  browserNotificationsSupported={browserNotificationsSupported}
                  notificationPermission={notificationPermission}
                  setNotificationPermission={setNotificationPermission}
                />
              </div>
            </div>
            <div className="col-span-4 md:col-span-1">
              <ProfessionPreferencesSection
                professions={professions}
                preferences={preferences}
                onChange={onChange}
              />
            </div>

          </div>
        )}
      </CardContent>
    </Card >
  );
};

export default NotificationPreferences;
